import { z } from 'zod';
import { facilityTypeSchema, resourcesSchema, shipTypeSchema } from './common.js';

/** What a building level does; the client words each key (info sheets, spec 2026-10-06 §3.3). */
export const CODEX_EFFECT_KEYS = [
  'max_building_level',
  'production_per_h',
  'storage_cap',
  'max_resources_per_trade',
  'ship_build_time_pct',
  'habitat_capacity_points',
  'odp_attack',
  'gate_speed_pct',
  'influence_per_h',
] as const;

export const codexEffectSchema = z.strictObject({
  key: z.enum(CODEX_EFFECT_KEYS),
  value: z.number(),
});

export const codexBuildingLevelSchema = z.strictObject({
  level: z.number().int().positive(),
  cost: resourcesSchema,
  durationMs: z.number().int().nonnegative(),
  effects: z.array(codexEffectSchema),
});

export const codexBuildingSchema = z.strictObject({
  type: facilityTypeSchema,
  maxLevel: z.number().int().positive(),
  /** Effects at level 0 (before the first upgrade), for "current" when a building is at 0. */
  baseEffects: z.array(codexEffectSchema),
  levels: z.array(codexBuildingLevelSchema),
});

export const codexShipSchema = z.strictObject({
  type: shipTypeSchema,
  line: z.enum(['front', 'mid', 'back', 'none']),
  attack: z.number(),
  defense: z.number(),
  hull: z.number(),
  speedLyph: z.number(),
  cargo: z.number().int(),
  habitatPoints: z.number().int(),
  cost: resourcesSchema,
  buildDurationMs: z.number().int().nonnegative(),
  dockLevel: z.number().int(),
});

export const codexResponseSchema = z.strictObject({
  buildings: z.array(codexBuildingSchema),
  ships: z.array(codexShipSchema),
  /** G-68: a timed fleet must leave within this many real hours (not scaled by galaxy speed). */
  travel: z.strictObject({ timedDispatchMaxAheadH: z.number().positive() }),
  /** G-85: a speed-up costs max(1, ceil(darkMatterPerHour × hours saved)). */
  speedUps: z.strictObject({ darkMatterPerHour: z.number().positive() }),
});
export type CodexResponse = z.infer<typeof codexResponseSchema>;
